import { useIdsSelect } from '@entities/common/IdsSelector/index.ts';
import { useSaveData } from '@entities/common/savedData/index';
import {
  useExerciseIdsSelect,
  useExercises,
  useTempExercises,
} from '@entities/exercise-video/model.ts';
import { useProgramEditorSavedFormState } from '@entities/programs/model.ts';
import { usePromo } from '@entities/promo-video/index.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useDescriptionEditor } from '@widgets/shared/descriptionEditor/model.ts';
import { useCurrentPos } from '@shared/hooks/use-current-pos.ts';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import { usePagesRedirect } from '@shared/hooks/use-pages-redirect.ts';
import { usePartitionsWithTagsSelection } from '@shared/hooks/use-partitions-with-tags-selection.ts';
import { useCloseFormDialogModal } from '@shared/hooks/useCloseFormDialogModal/useCloseFormDialogModal.ts';
import type { ExerciseVideo } from '@shared/types/exercises';
import type { Program } from '@shared/types/programs';
import { useProgramQuery } from '@src/api/api-hooks/programs/useProgramQuery.ts';
import { programCreateSchema, programUpdateSchema, type ProgramEditorValues } from '../lib';

const getParsedExercises = (videos: ExerciseVideo[]) => {
  return videos.map((video) => video.id);
};

const getParsedProgram = (program: Program): ProgramEditorValues => {
  return {
    title: program.title,
    description: program.description,
    cost_base: program?.cost_base ? program.cost_base / 100 : undefined,
    costs: program.costs,
    periodicity: program.periodicity,
    schedule: program.schedule,
    schedules: program.schedules,
    module: program.module,
    tags: program.tags ? program.tags.map((tag) => tag.id) : [],
    excluded_videos_period: program.excluded_videos_period,
    promo_image: program.promo_image,
    promo_video: program?.promo_video?.id || 0,
    exercises: getParsedExercises(program.videos),
    access_duration: program?.access_duration?.id ? Number(program.access_duration.id) : 1,
    with_comments: program.with_comments,
    with_likes: program.with_likes,
    partitions: program.partitions.map((p) => p.id) || [],
  };
};

export const useEditorForm = () => {
  const { doCloseFormWithDialogModal } = useCloseFormDialogModal();
  const { id, code = '' } = useParams<{ id: string; code: string }>();
  const { addExercises } = useExercises();
  const { doRedirectOnPrevPage } = usePagesRedirect('programEditor');
  const programId = Number(id);
  const { moduleId } = useModuleByCodeParam();
  const _HASH = `ProgramEditorPage${id}`;

  const { clearStorage: clearDescription } = useDescriptionEditor(_HASH);

  const { data: programDataFromQuery, isLoading: isProgramLoading } = useProgramQuery({
    id: id === 'new' ? undefined : id,
  });

  const { clearTempExercisesStorage, addTempExercise, tempExerciseIds, removeTempExercise } =
    useTempExercises();

  useEffect(() => {
    programDataFromQuery?.videos && addExercises(programDataFromQuery?.videos);
    programDataFromQuery?.videos.forEach(
      (el) => !el.in_library && !tempExerciseIds.includes(el.id) && addTempExercise(el, false)
    );
  }, [programDataFromQuery, tempExerciseIds, addExercises]);

  const {
    programEditorSavedFormState,
    doSetProgramEditorSavedFormState,
    doRemoveProgramEditorSavedFormState,
  } = useProgramEditorSavedFormState();

  const { clearPartitionsData, selectedPartitions, selectedTags } = usePartitionsWithTagsSelection({
    hash: _HASH,
    initializeData: {
      tags: programDataFromQuery?.tags || [],
      partitions: programDataFromQuery?.partitions || [],
      savedFormState: programEditorSavedFormState,
    },
  });

  const { doResetSelectedIds: resetSelectedGroupIds, selectedIds: selectedGroupIds } =
    useIdsSelect('groups');

  const {
    data: promoVideoId,
    doRemoveStorageData: removePromoVideoId,
    doSetStorageData: setPromoVideoId,
  } = useSaveData<number>('promoVideoId');

  const {
    selectedIds: selectedExerciseIds,
    doRemoveId,
    doResetSelectedIds: resetSelectedExerciseIds,
  } = useExerciseIdsSelect();

  // const { selectedTagIds, doResetSelectedTagIds } = useTagIdsSelect();

  const clearStorage = useCallback(() => {
    setTimeout(() => {
      // console.log('CLEANING');
      clearDescription();
      clearPartitionsData();
      removePromoVideoId();
      clearTempExercisesStorage();
      resetSelectedExerciseIds([]);
      resetSelectedGroupIds([]);
      doRemoveProgramEditorSavedFormState();
    }, 0);
  }, [
    clearPartitionsData,
    doRemoveProgramEditorSavedFormState,
    removePromoVideoId,
    clearTempExercisesStorage,
    resetSelectedExerciseIds,
    clearDescription,
    resetSelectedGroupIds,
  ]);

  const promoVideoIdSelector = useMemo(
    () =>
      promoVideoId ||
      programEditorSavedFormState?.promo_video ||
      programDataFromQuery?.promo_video?.id ||
      0,
    [promoVideoId, programEditorSavedFormState, programDataFromQuery]
  );

  const defaultData =
    (programEditorSavedFormState && {
      ...programEditorSavedFormState,
      exercises: selectedExerciseIds,
      partitions: selectedPartitions,
      module: moduleId,
      promo_video: promoVideoIdSelector,
      tags: selectedTags,
    }) ||
    (programDataFromQuery && {
      ...getParsedProgram(programDataFromQuery),
    });

  const schema = isNaN(programId) ? programCreateSchema : programUpdateSchema;

  const { reset, watch, getValues, register, setValue, ...methods } = useForm<ProgramEditorValues>({
    resolver: yupResolver(schema as any),
    defaultValues: {
      module: moduleId,
      exercises: selectedExerciseIds || [],
      promo_video: promoVideoIdSelector,
      tags: selectedTags,
      access_duration: 1,
      periodicity: false,
      schedule: false,
      schedules: [],
      excluded_videos_period: [[], [], [], [], [], [], []],
      cost_base: undefined,
      costs: [],
      with_likes: false,
      with_comments: false,
    },
    values: { ...defaultData },
  });

  const [isModuleSelectedAfterTryVideoAdd, setIsModuleSelectedAfterTryVideoAdd] = useState(true);

  const { removePromo: removePromoFormStore, setPromo } = usePromo(promoVideoId);

  const removeExercise = useCallback(
    (id: number) => {
      tempExerciseIds.includes(id) ? removeTempExercise(id) : doRemoveId(id);
    },
    [tempExerciseIds, doRemoveId, removeTempExercise]
  );

  const removePromo = useCallback(() => {
    setValue('promo_video', 0, { shouldDirty: true });
    removePromoVideoId();
    removePromoFormStore(promoVideoId);
    methods.trigger('promo_video');
  }, [promoVideoId, removePromoVideoId, removePromoFormStore, methods, setValue]);

  const hasDirtyFields = useMemo(() => {
    const {
      formState: { dirtyFields },
    } = { ...methods };
    const module = getValues('module');
    const isDirty = Object.keys(dirtyFields).length > 0;
    return module !== 0 || Boolean(selectedGroupIds) || isDirty;
  }, [methods, getValues, selectedGroupIds]);

  const doClose = useCallback(() => {
    doRedirectOnPrevPage();
    clearStorage();
  }, [doRedirectOnPrevPage, clearStorage]);

  const doSaveFormData = useCallback(() => {
    doSetProgramEditorSavedFormState(getValues() as any);
  }, [doSetProgramEditorSavedFormState, getValues]);
  const { handleClick: handleRedirect } = useCurrentPos('scroll', doSaveFormData);

  const isSubmitDisabled = false;

  return {
    doRemoveProgramEditorSavedFormState,
    doSetProgramEditorSavedFormState,
    methods: { reset, watch, getValues, setValue, register, ...methods },
    removeExercise,
    removePromo,
    hasDirtyFields,
    resetSelectedExerciseIds,
    setPromoVideoId,
    setPromo,
    removePromoVideoId,
    handleRedirect,
    clearStorage,
    programId,
    code,
    id,
    isSubmitDisabled,
    isModuleSelectedAfterTryVideoAdd,
    setIsModuleSelectedAfterTryVideoAdd,
    programDataFromQuery,
    defaultData,
    isProgramLoading,
    register,
    _HASH,
    errors: methods.formState.errors,
    onClose: doCloseFormWithDialogModal({
      onModalConfirm: doClose,
      isShouldModalOpen: hasDirtyFields,
    }),
  };
};
